// SPDX-FileCopyrightText: 2026 Ben Richardson
// SPDX-License-Identifier: AGPL-3.0-or-later

import { openOverlay } from './overlay';
import { num, longDate, escapeHtml } from './format';
import type { Core, Meta } from './types';

export function openAbout(core: Core, meta: Meta): void {
  const gatesPassed = meta.gates.filter((g) => g.pass).length;
  openOverlay({
    title: 'About this site',
    body: `
      <h3>What this is</h3>
      <p>Every Australian honour on the public record — ${num(core.totals.records)} awards
      gazetted between 1877 and today — read in full from the Australian Government's
      <em>It's an Honour</em> register and turned into something you can search, map and
      measure.</p>

      <h3>Where the data comes from</h3>
      <p>The register is published by the Department of the Prime Minister and Cabinet at
      <a href="https://honours.pmc.gov.au/honours/search" target="_blank" rel="noopener">honours.pmc.gov.au</a>.
      It is an excellent single-record lookup, but it publishes no totals, no geography and
      no analysis — it cannot tell you how many records it holds. This site reads the whole
      register and answers the questions the official one structurally cannot.</p>
      <p>Population, income and boundary data are from the Australian Bureau of Statistics
      2021 Census (Census DataPacks G01 and G02 by postal area, and ASGS 2021 postal area
      boundaries).</p>

      <h3>How current it is</h3>
      <p>The most recent award in this extract is dated <strong>${longDate(meta.as_at_award_date)}</strong>.
      The extract was taken on ${longDate(meta.fetched_at.slice(0, 10))} and refreshes
      quarterly, which matches the register's own rhythm: two big lists a year (Australia Day
      in late January and the King's Birthday in June) with bravery, meritorious and long-service
      awards gazetted continuously in between.</p>

      <h3>What it can't tell you</h3>
      <ul>
        <li><strong>Most records have no location.</strong> A postcode is published for only
        ${((core.totals.withPostcode / core.totals.records) * 100).toFixed(0)}% of the register,
        but for ${((core.totals.oaWithPostcode / core.totals.oa) * 100).toFixed(0)}% of Order of
        Australia records. Every map and per-capita figure here is therefore an
        <strong>Order of Australia</strong> measure, and says so.</li>
        <li><strong>One award dominates the raw counts.</strong> The National Medal — a
        15-year long-service medal — is about two in five records. "Most common honour" is not
        the same as "most honoured achievement".</li>
        <li><strong>There is no sex in the data.</strong> The register publishes none. The only
        signal is the title a person is gazetted under, and it fails hardest at the top: it
        cannot classify ${((core.sex.AC.unknown / core.sex.AC.total) * 100).toFixed(0)}% of
        Companions against ${((core.sex.OAM.unknown / core.sex.OAM.total) * 100).toFixed(0)}%
        of Medal recipients, because senior figures are gazetted as Professor, Dr or by military
        rank. That is why no "the gender gap widens with rank" claim appears here, and why the
        unclassified share is drawn on the chart rather than hidden.</li>
        <li><strong>Service domains are our reading, not the register's.</strong> The subject
        groupings are derived by classifying citation text and are an editorial judgement.</li>
        <li><strong>Awards are located where the recipient was gazetted</strong>, which is not
        necessarily where they did the work, and is compared against a 2021 Census denominator.</li>
      </ul>

      <h3>How the numbers are checked</h3>
      <p>The pipeline runs ${meta.gates.length} reconciliation gates on every build and refuses
      to publish if any fails; ${gatesPassed} passed on this one. They include an exact
      three-way match of the extract against the register's own reported total, a check that the
      award-system merge consumed every abbreviated code, and two gates that assert known
      <em>limitations</em> remain true so a caveat cannot silently stop applying.</p>
      <details>
        <summary>See all ${meta.gates.length} checks</summary>
        <ul>${meta.gates
          .map((g) => `<li><strong>${escapeHtml(g.name)}</strong> — ${escapeHtml(g.detail)}</li>`)
          .join('')}</ul>
      </details>

      <h3>Privacy, and correcting a record</h3>
      <p>These are named people, and publication in the register is <strong>voluntary and
      revocable</strong>: recipients consent before their entry appears and can ask for it to be
      removed. This site takes a complete fresh snapshot every run and replaces its data
      outright — it never merges old records forward — so a removal at the source flows through
      here. It publishes only the fields the register already shows, creates no per-person page
      or per-person link, and never joins these records to any other dataset.</p>
      <p>To correct or remove an entry, contact the Australian Honours and Awards Secretariat at
      <a href="mailto:honours@gg.gov.au">honours@gg.gov.au</a> — that is the authoritative record,
      and changes there will appear here at the next refresh.</p>

      <h3>Licence and attribution</h3>
      <p>Honours data © Commonwealth of Australia 2023, used under a
      <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener">Creative Commons Attribution 4.0 International licence</a>
      (see the department's
      <a href="https://www.pmc.gov.au/copyright-and-disclaimer" target="_blank" rel="noopener">copyright notice</a>).
      Census and boundary data © Australian Bureau of Statistics, CC BY 4.0. This site is not
      affiliated with, or endorsed by, the Australian Government.</p>
    `,
  });
}
